import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { EyeIcon, EnvelopeIcon, PhoneIcon } from '@heroicons/react/24/outline';
import {
  Card,
  Table,
  SearchInput,
  Badge,
  Pagination,
  Avatar,
  Button,
  EmptyState,
} from '../components/ui';
import { usePatientStore } from '../stores';
import type { Patient } from '../types';
import { formatDate, formatPhone } from '../utils/helpers';

export const Patients: React.FC = () => {
  const navigate = useNavigate();
  const {
    patients,
    isLoading,
    totalCount,
    currentPage,
    pageSize,
    filters,
    fetchPatients,
    setFilters,
    setPage,
  } = usePatientStore();

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients, currentPage, filters]);

  const handleSearch = useCallback(
    (value: string) => {
      setFilters({ search: value });
    },
    [setFilters]
  );

  const handlePageChange = (page: number) => {
    setPage(page);
  };

  const columns = [
    {
      key: 'name',
      header: 'Patient',
      render: (patient: Patient) => (
        <div className="flex items-center space-x-3">
          <Avatar firstName={patient.firstName} lastName={patient.lastName} size="md" />
          <div>
            <p className="font-medium text-secondary-900 dark:text-secondary-100">
              {patient.firstName} {patient.lastName}
            </p>
            <p className="text-sm text-secondary-500 dark:text-secondary-400">ID: {patient.id.slice(0, 8)}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'contact',
      header: 'Contact',
      render: (patient: Patient) => (
        <div className="space-y-1">
          <div className="flex items-center text-sm text-secondary-600 dark:text-secondary-300">
            <EnvelopeIcon className="h-4 w-4 mr-2 text-secondary-400 dark:text-secondary-500" />
            {patient.email}
          </div>
          <div className="flex items-center text-sm text-secondary-600 dark:text-secondary-300">
            <PhoneIcon className="h-4 w-4 mr-2 text-secondary-400 dark:text-secondary-500" />
            {formatPhone(patient.phone)}
          </div>
        </div>
      ),
    },
    {
      key: 'location',
      header: 'Location',
      render: (patient: Patient) => (
        <span className="text-secondary-600 dark:text-secondary-300">
          {patient.city}, {patient.state}
        </span>
      ),
    },
    {
      key: 'lastVisit',
      header: 'Last Visit',
      sortable: true,
      render: (patient: Patient) => (
        <span className="text-secondary-600 dark:text-secondary-300">
          {patient.lastVisit ? formatDate(patient.lastVisit) : 'N/A'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: () => (
        <Badge variant="success" dot>
          Active
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-12',
      render: (patient: Patient) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/patients/${patient.id}`)}
        >
          <EyeIcon className="h-4 w-4" />
        </Button>
      ),
    },
  ];

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">Patients</h1>
        <p className="text-secondary-500 dark:text-secondary-400">View and manage your patient records</p>
      </div>

      {/* Search */}
      <Card padding="sm">
        <SearchInput
          value={filters.search}
          onChange={handleSearch}
          placeholder="Search patients by name, email, or ID..."
          className="max-w-md"
        />
      </Card>

      {/* Table */}
      <Card padding="none">
        {patients.length === 0 && !isLoading ? (
          <EmptyState
            title="No patients found"
            description="Patients will appear here when they are added to your account"
          />
        ) : (
          <>
            <Table
              columns={columns}
              data={patients}
              keyExtractor={(patient) => patient.id}
              isLoading={isLoading}
              onRowClick={(patient) => navigate(`/patients/${patient.id}`)}
            />
            {totalPages > 1 && (
              <div className="p-4 border-t border-secondary-100 dark:border-secondary-700">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default Patients;



